import Title from 'antd/lib/typography/Title'
import {
    TextAnnotationType,
    SingleValueDataType,
    ValueTypeDefinitions,
    UseCaseTypes,
} from 'Types/Tasks.types'
import styles from './TextAnnotationsTaskUI.module.less'
import { useEffect, useRef, useState } from 'react'
import { AnnotationSelection, ClassAnnotation, ClassItem } from 'Types/Classes.types'
import { TextAnnotationsContent } from 'Tasks/TextAnnotationTask/UI/TextAnnotationsContent'
import { TextAnnotationsComment } from 'Tasks/TextAnnotationTask/UI/TextAnnotationsComment'
import {
    getContainerElementId,
    hasParentWithId,
    splitSelectionIntoIndividualRanges,
} from 'Utils/dom.utils'
import { PromptContent } from 'Components/tasks/Prompt/Prompt'
import { isAnnotationOverlapping } from 'Utils/task.utils'
import StopOutlined from '@ant-design/icons/lib/icons/StopOutlined'
import { useMessage } from 'shared/hooks/use-message'
import { useTaskContext } from 'Contexts/TaskContext'

export type TextAnnotationsTaskUiProps = {
    content: string | SingleValueDataType<keyof ValueTypeDefinitions>[][]
    getMaxHeightIndex: () => number
    getSelectedClass: () => TextAnnotationType | null
    getAnnotationList: () => ClassAnnotation[]
    addAnnotationToList: (selection: AnnotationSelection) => void
    deleteAnnotationFromList: (index: number) => void
    onChangeContainerDimensions: () => void
    getClassList: () => ClassItem[]
    onInit: () => void
    updateAnnotationComment: (annotationId: string, comment: string) => void
    getFocusedAnnotationId: () => string | null
    setFocusedAnnotationId: (id: string | null) => void
}

export const TextAnnotationsUI = (props: TextAnnotationsTaskUiProps) => {
    const [isCreatingAnnotation, setIsCreatingAnnotation] = useState(false)
    const [commnetsAnnotationId, setCommentsAnnotationId] = useState<string | null>(null)
    const [offset, setOffset] = useState({ top: 0, left: 0, width: 0 })
    const contentContainerRef = useRef<HTMLDivElement>(null)
    const [messageApi] = useMessage()
    const { studio, isReadOnly } = useTaskContext()
    const isOverlappingAllowed =
        studio.taskConfig.assignment.task.config.text_annotations_config?.is_overlapping_allowed ??
        false

    useEffect(() => {
        props.onInit()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const element = contentContainerRef.current
        const mainContentElement = document.getElementById('main-content')

        if (!element || !mainContentElement) return

        const updateOffset = () => {
            const rect = element.getBoundingClientRect()
            const offsetTop = rect.top + window.scrollY
            const offsetLeft = rect.left + window.scrollX
            setOffset({ top: offsetTop, left: offsetLeft, width: rect.width })
            props.onChangeContainerDimensions() // Call the function to update positions when size or scroll changes
        }

        const resizeObserver = new ResizeObserver(entries => {
            entries.forEach(() => {
                updateOffset()
            })
        })

        const handleScroll = () => {
            updateOffset()
        }

        mainContentElement.addEventListener('scroll', handleScroll)

        resizeObserver.observe(element)

        // Clean up the observer on component unmount
        return () => {
            if (element) {
                resizeObserver.unobserve(element)
            }
            if (mainContentElement) {
                mainContentElement.removeEventListener('scroll', handleScroll)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSelectContent = () => {
        if (!props.getSelectedClass()) {
            return
        }

        const windowSelection = window.getSelection()
        if (
            !windowSelection ||
            windowSelection.type !== 'Range' ||
            !hasParentWithId(windowSelection.focusNode, 'text-annotations-content') ||
            !hasParentWithId(windowSelection.anchorNode, 'text-annotations-content')
        ) {
            return
        }

        // If a selection spans across multiple elements in case of multi media grid, split it into multiple ranges
        const ranges = splitSelectionIntoIndividualRanges(windowSelection)

        ranges.forEach(range => {
            const containerElementId = getContainerElementId(range.startContainer)

            if (containerElementId === null) {
                return
            }

            if (
                !isOverlappingAllowed &&
                isAnnotationOverlapping(
                    props.getAnnotationList(),
                    containerElementId,
                    range.startOffset,
                    range.endOffset
                )
            ) {
                windowSelection.removeAllRanges()
                messageApi.error({
                    content: 'Overlapping annotations are not supported',
                    icon: <StopOutlined />,
                })
                return
            }

            props.addAnnotationToList({
                startIndex: range.startOffset,
                endIndex: range.endOffset,
                individualRowSelectionRange: Array.from(range.getClientRects()).map(rect => ({
                    positionStartX: rect.x,
                    positionStartY: rect.y,
                    positionWidth: rect.width,
                    index: 0,
                })),
                range: range,
                fieldPath: containerElementId,
            })
        })
    }

    const onMouseDown = () => {
        setIsCreatingAnnotation(true)
    }

    const onMouseUp = () => {
        setIsCreatingAnnotation(false)
    }

    return (
        <div
            className={styles['text-annotations-root']}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            data-testid="text-annotations-task-ui-wrapper"
        >
            <Title className={styles['title']}>Response</Title>
            <div
                className={styles['text-annotations-content']}
                style={{
                    lineHeight: props.getMaxHeightIndex()
                        ? `${(props.getMaxHeightIndex() + 1) * 1.9}`
                        : '1.6',
                }}
                ref={contentContainerRef}
                id="text-annotations-content"
                onMouseUp={onSelectContent}
            >
                <PromptContent
                    useCase={UseCaseTypes.TEXT_ANNOTATION}
                    value={props.content}
                    type={typeof props.content === 'string' ? 'STRING' : 'MULTI_MEDIA_GRID'}
                    id="content"
                />
            </div>

            <TextAnnotationsContent
                annotationsList={props.getAnnotationList()}
                classList={props.getClassList()}
                offset={offset}
                maxElementsInRow={props.getMaxHeightIndex()}
                onClickDeleteAnnotations={props.deleteAnnotationFromList}
                selectedClass={props.getSelectedClass()}
                isCreatingAnnotation={isCreatingAnnotation}
                setCommentsAnnotationId={setCommentsAnnotationId}
                focusedAnnotationId={props.getFocusedAnnotationId()}
                setFocusedAnnotationId={props.setFocusedAnnotationId}
                isReadOnly={isReadOnly()}
            />

            <TextAnnotationsComment
                selectedAnnotationId={commnetsAnnotationId}
                resetAnnotationId={() => setCommentsAnnotationId(null)}
                selectedAnnotationSelection={
                    props
                        .getAnnotationList()
                        .find(annotation => annotation.id === commnetsAnnotationId)?.selection
                }
                comment={
                    props
                        .getAnnotationList()
                        .find(annotation => annotation.id === commnetsAnnotationId)?.comment ?? ''
                }
                offset={offset}
                updateAnnotationComment={props.updateAnnotationComment}
            />
        </div>
    )
}
